import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useForm, z, zodResolver } from "@/hooks/schema";
import { Form } from "@/components/Form";
import { Check, Pencil, ImageUp, Trash } from "lucide-react";
import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { getShowUnities, updateUnitiesData } from "@/Api/unities";
import { useToastNavigate } from "@/utils/toastNavigate";
import { LoaderSpin } from "@/components/Loaders";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";

const schema = z.object({
  name: z.string({ message: "*requerido" }).min(1, "*requerido"),
  image: z.any(),
  has_stockist: z.boolean().default(false),
  seller_received_without_distribution: z.boolean().default(false),
  belongs_to_sale: z.boolean().default(true),
  has_bleed: z.boolean().default(false),
});

type SchemaType = z.infer<typeof schema>;

export const UpdateBtn = ({
  updateFront,
  uuid,
}: {
  updateFront: (response: any) => void;
  uuid: string;
}) => {
  const form = useForm<SchemaType>({
    resolver: zodResolver(schema),
  });

  const { toast } = useToastNavigate();
  const [formState, setFormState] = useState<string>("default");
  const [isOpen, setIsOpen] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (isOpen) {
        const { data: responseApi } = await getShowUnities(uuid);
        form.setValue("name", responseApi?.response?.name || "");
        form.setValue("image", responseApi?.response?.image || null);
        form.setValue("has_stockist", responseApi?.response?.has_stockist);
        form.setValue("seller_received_without_distribution", responseApi?.response?.seller_received_without_distribution);
        form.setValue("belongs_to_sale", responseApi?.response?.belongs_to_sale ?? true);
        form.setValue("has_bleed", !!responseApi?.response?.has_bleed);
        setImagePreview(responseApi?.response?.image || null);
      }
    };

    fetchData();
  }, [isOpen, uuid, form]);

  const handleImageChange = (file: File | null) => {
    form.setValue("image", file);
    if (file) {
      setImagePreview(URL.createObjectURL(file));
    } else {
      setImagePreview(null);
    }
  };

  const formSubmit = async (data: SchemaType) => {
    setFormState("loading");
    const payload = {
      ...data,
      image: data.image instanceof File ? data.image : null,
      has_stockist: data.has_stockist ? 1 : 0,
      seller_received_without_distribution: data.seller_received_without_distribution ? 1 : 0,
      belongs_to_sale: data.belongs_to_sale ? 1 : 0,
      has_bleed: data.has_bleed ? 1 : 0,
    };

    const { data: apiResponse, ok } = await updateUnitiesData(uuid, payload);
    if (!ok) {
      setFormState("error");
      setIsOpen(false);
      return;
    }

    updateFront(apiResponse?.response);
    toast({
      description: `Unidade atualizada com sucesso!`,
      action: <Check color="#1edc2b" />,
    });

    setFormState("default");
    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="size-7 p-0 text-slate-900 hover:bg-stone-950/10 dark:text-[#F4F4F5]"
          onClick={() => setIsOpen(true)}
        >
          <Pencil className="size-5" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader className="flex flex-col gap-2">
          <DialogDescription className="text-lg">
            Editar Unidade
          </DialogDescription>
          <DialogTitle className="text-2xl">Informações Básicas</DialogTitle>
        </DialogHeader>
        <Form
          onSubmit={form.handleSubmit(formSubmit)}
          {...form}
          className="space-y-3"
        >
          <Form.Row>
            <Form.Field name="name">
              {({ field }) => (
                <Form.Item>
                  <Form.Label>
                    Nome <Form.Message />
                  </Form.Label>
                  <Form.Control>
                    <Input {...field} placeholder="Insira um nome" />
                  </Form.Control>
                </Form.Item>
              )}
            </Form.Field>
          </Form.Row>

          <Form.Row>
            <Form.Field name="has_stockist">
              {({ field }) => (
                <Form.Item className="flex flex-row items-start space-x-3 space-y-0">
                  <Form.Control>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </Form.Control>
                  <div className="space-y-1 leading-none">
                    <Form.Label>
                      Tem Estoquista
                    </Form.Label>
                  </div>
                </Form.Item>
              )}
            </Form.Field>
          </Form.Row>

          <Form.Row>
            <Form.Field name="seller_received_without_distribution">
              {({ field }) => (
                <Form.Item className="flex flex-row items-start space-x-3 space-y-0">
                  <Form.Control>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </Form.Control>
                  <div className="space-y-1 leading-none">
                    <Form.Label>
                      Vendedor Recebe Sem Distribuição
                    </Form.Label>
                  </div>
                </Form.Item>
              )}
            </Form.Field>
          </Form.Row>

          <Form.Row>
            <Form.Field name="belongs_to_sale">
              {({ field }) => (
                <Form.Item className="flex flex-row items-start space-x-3 space-y-0">
                  <Form.Control>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </Form.Control>
                  <div className="space-y-1 leading-none">
                    <Form.Label>
                      Pertence a Vendas e Distribuição
                    </Form.Label>
                  </div>
                </Form.Item>
              )}
            </Form.Field>
          </Form.Row>

          <Form.Row>
            <Form.Field name="has_bleed">
              {({ field }) => (
                <Form.Item className="flex flex-row items-start space-x-3 space-y-0">
                  <Form.Control>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </Form.Control>
                  <div className="space-y-1 leading-none">
                    <Form.Label>
                      Sangria
                    </Form.Label>
                    <p className="text-xs text-muted-foreground">
                      Vendedores desta unidade registram sangrias e usam "Dinheiro - Sangria" no lugar de "Dinheiro" nas vendas.
                    </p>
                  </div>
                </Form.Item>
              )}
            </Form.Field>
          </Form.Row>

          <div className="mt-5">
            <label className="text-1xl font-medium text-gray-900 dark:text-zinc-300">
              Foto:
            </label>
            <div className="flex justify-center itens-center rounded-md border border-dashed dark:border-gray-500 border-gray-900/25 px-2 py-3 mt-2">
              <div className="flex flex-col items-center gap-4">
                {imagePreview ? (
                  <img
                    className="rounded-md aspect-square w-40 object-cover"
                    src={imagePreview}
                    alt="Logo preview"
                  />
                ) : (
                  <ImageUp className="mx-auto size-12 text-gray-300 mt-2" />
                )}
                <div className="flex justify-center items-center gap-5 text-sm/6 text-gray-600">
                  <label
                    htmlFor="file-upload"
                    className="relative cursor-pointer rounded-md bg-transparent font-semibold text-blue-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-600 focus-within:ring-offset-2 hover:text-blue-700"
                  >
                    <span className="flex items-center gap-1">
                      {imagePreview && <ImageUp className="size-5" />}
                      Procurar imagem
                    </span>
                    <input
                      id="file-upload"
                      type="file"
                      className="sr-only"
                      accept="image/*"
                      onChange={(e) =>
                        handleImageChange(e.target.files?.[0] || null)
                      }
                    />
                  </label>
                  {imagePreview && (
                    <Button
                      className="h-6 px-0 bg-transparent text-slate font-medium hover:text-red-600 hover:bg-transparent flex gap-1"
                      onClick={() => handleImageChange(null)}
                    >
                      <Trash className="size-5" />
                      Remover
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="submit"
              className={cn("w-full", colors.bg_primary)}
              disabled={formState !== "default"}
            >
              {formState === "loading" && (
                <>
                  Atualizando <LoaderSpin />
                </>
              )}
              {formState === "default" && "Atualizar"}
              {formState === "error" && "Erro ao atualizar"}
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
